import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { PlatformMark } from "./PlatformMark";
import { Sticker } from "./Sticker";

/**
 * Crosslist: post this item to each storefront. Thrifty queues the posts; the
 * paired browser extension (Depop, Poshmark, Vinted) or the server (eBay)
 * carries them out, and when the item sells anywhere the others come down.
 */

interface Preview {
  title: string;
  description: string;
  price: number | null;
  condition: string | null;
  category: string[];
  colors: string[];
  sizeLabel: string | null;
  photos: string[];
  photoSpec: { aspect: number | null; long: number; max: number };
  warnings: string[];
}

interface PlatformState {
  platform: string;
  mode: "extension" | "server";
  ready: boolean;
  readyNote: string | null;
  listing: {
    id: string; status: string; listPrice: string | null;
    externalId: string | null; externalUrl: string | null; syncError: string | null;
  } | null;
  preview: Preview;
}

interface Job {
  id: string; platform: string; action: string; status: string;
  attempts: number; lastError: string | null; createdAt: string;
}

interface CrosslistData {
  itemStatus: string;
  platforms: PlatformState[];
  jobs: Job[];
  devices: Array<{ id: string; name: string; online: boolean; lastSeenAt: string | null }>;
}

const CLOSED = ["sold", "shipped", "donated", "lost", "damaged"];

const photoLabel = (s: Preview["photoSpec"]) =>
  s.aspect === 1 ? `square ${s.long}px` : `${s.long}px long side`;

export function CrosslistPanel({ itemId, onChange }: { itemId: string; onChange?: () => void }) {
  const [data, setData] = useState<CrosslistData | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The parent passes a fresh callback each render; keep the poll timer steady.
  const changed = useRef(onChange);
  changed.current = onChange;

  const load = useCallback(async () => {
    try {
      const d = await api.get<CrosslistData>(`/items/${itemId}/crosslist`);
      setData(d);
      setPrices((prev) => {
        const next = { ...prev };
        for (const p of d.platforms) {
          if (next[p.platform] === undefined) {
            const v = p.listing?.listPrice ?? (p.preview.price !== null ? String(p.preview.price) : "");
            next[p.platform] = v ? Number(v).toFixed(2) : "";
          }
        }
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load crosslisting");
    }
  }, [itemId]);

  useEffect(() => { void load(); }, [load]);

  // While anything is queued or running, keep the statuses fresh.
  const active = data?.jobs.some((j) => j.status === "queued" || j.status === "running");
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => { void load(); changed.current?.(); }, 10_000);
    return () => clearInterval(t);
  }, [active, load]);

  if (!data) return error ? <div className="notice">{error}</div> : null;
  const closed = CLOSED.includes(data.itemStatus);

  const choosable = data.platforms.filter((p) => !(p.listing?.status === "active" && p.listing.externalId));
  const chosen = choosable.filter((p) => picked[p.platform]);

  async function post() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/items/${itemId}/crosslist`, {
        platforms: chosen.map((p) => ({ platform: p.platform, price: Number(prices[p.platform]) })),
      });
      setPicked({});
      await load();
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not queue those posts");
    } finally {
      setBusy(false);
    }
  }

  async function remote(listingId: string, action: "publish" | "delist") {
    if (action === "delist" && !window.confirm("Take this listing down on the site itself?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/listings/${listingId}/remote`, { action });
      await load();
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not queue that");
    } finally {
      setBusy(false);
    }
  }

  const lastJob = (platform: string) => data.jobs.find((j) => j.platform === platform);
  const online = data.devices.filter((d) => d.online);

  return (
    <section className="rail">
      <h2 className="eyebrow rail-label">Crosslist</h2>
      {error && <div className="notice">{error}</div>}

      {data.platforms.map((p) => {
        const job = lastJob(p.platform);
        const live = p.listing?.status === "active" && p.listing.externalId;
        const invalidPrice = !(Number(prices[p.platform]) > 0);
        return (
          <div key={p.platform} className="tag" style={{ cursor: "default", alignItems: "flex-start" }}>
            <PlatformMark platform={p.platform} />
            <span className="tag-body">
              <span className="tag-title" style={{ textTransform: "capitalize" }}>
                {!closed && !live && (
                  <input
                    type="checkbox"
                    aria-label={`Post to ${p.platform}`}
                    checked={Boolean(picked[p.platform])}
                    onChange={(e) => setPicked({ ...picked, [p.platform]: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                )}
                {p.platform}
              </span>
              <span className="tag-meta">
                {p.listing ? <Sticker status={p.listing.status} /> : <span>Not listed</span>}
                {p.listing?.externalUrl && (
                  <a href={p.listing.externalUrl} target="_blank" rel="noreferrer">Open listing</a>
                )}
                {job && (job.status === "queued" || job.status === "running") && (
                  <span>{job.action === "delist" ? "Taking down" : "Posting"}: {job.status}</span>
                )}
                {job?.status === "failed" && <span style={{ color: "var(--tag-alert)" }}>{job.action} failed</span>}
              </span>
              {p.listing?.syncError && (
                <span className="hint" style={{ color: "var(--tag-alert)" }}>{p.listing.syncError}</span>
              )}
              {!p.ready && p.readyNote && !closed && <span className="hint">{p.readyNote}</span>}
              {p.preview.warnings.length > 0 && !closed && !live && (
                <span className="hint">Check: {p.preview.warnings.join(" · ")}</span>
              )}
              <span className="row-actions">
                <button className="quiet" onClick={() => setOpen(open === p.platform ? null : p.platform)}>
                  {open === p.platform ? "Hide preview" : "Preview"}
                </button>
                {p.listing?.status === "error" && (
                  <button className="quiet" onClick={() => void remote(p.listing!.id, "publish")} disabled={busy}>
                    Try again
                  </button>
                )}
                {live && (
                  <button className="quiet danger" onClick={() => void remote(p.listing!.id, "delist")} disabled={busy}>
                    Take down
                  </button>
                )}
              </span>
              {open === p.platform && (
                <span className="crosslist-preview" style={{ display: "block", marginTop: 8 }}>
                  {p.preview.title && <strong style={{ display: "block" }}>{p.preview.title}</strong>}
                  <span className="hint" style={{ display: "block" }}>
                    {[p.preview.category.join(" › "), p.preview.sizeLabel && `Size ${p.preview.sizeLabel}`,
                      p.preview.condition, p.preview.colors.join(", ")].filter(Boolean).join(" · ")}
                  </span>
                  <span className="hint" style={{ display: "block" }}>
                    {Math.min(p.preview.photos.length, p.preview.photoSpec.max)} photos, {photoLabel(p.preview.photoSpec)}
                  </span>
                  <span style={{ display: "block", whiteSpace: "pre-wrap", marginTop: 6, fontSize: 13 }}>
                    {p.preview.description}
                  </span>
                </span>
              )}
            </span>
            <span className="tag-price">
              {!closed && !live ? (
                <input
                  className="money" type="number" step="0.01" min="0" style={{ width: 84 }}
                  aria-label={`${p.platform} price`}
                  value={prices[p.platform] ?? ""}
                  onChange={(e) => setPrices({ ...prices, [p.platform]: e.target.value })}
                  aria-invalid={picked[p.platform] && invalidPrice}
                />
              ) : (
                <span className="ask">{money(p.listing?.listPrice ?? null)}</span>
              )}
            </span>
          </div>
        );
      })}

      {!closed && (
        <div className="actions">
          <button
            onClick={() => void post()}
            disabled={busy || chosen.length === 0 || chosen.some((p) => !(Number(prices[p.platform]) > 0))}
          >
            {busy ? "Queueing" : chosen.length > 1 ? `Post to ${chosen.length} sites` : "Post"}
          </button>
        </div>
      )}
      <p className="hint">
        {online.length > 0
          ? `Posting runs in ${online.map((d) => d.name).join(" and ")}. When it sells on one site, Thrifty takes it down on the others.`
          : "No paired browser is online. Posts wait in the queue until one is (Settings → Browser extension)."}
      </p>
    </section>
  );
}
